import { type ReactNode, type Ref } from 'react';
import classnames from 'classnames';
import { noop } from 'libs';

const VARIANT_BORDER = 'border';
const VARIANT_BORDER_DANGER = 'border-danger';
const VARIANT_DANGER = 'danger';
const VARIANT_DEFAULT = 'default';

const VARIANT_LINK_INLINE = 'link-inline';
const VARIANT_LINK_INLINE_RED = 'link-inline-red';
const VARIANT_LINK_INLINE_STRONG = 'link-inline-strong';

const VARIANT_PRIMARY_DARK = 'primary-dark';
const VARIANT_PRIMARY_LIGHT = 'primary-light';

const SIZE_DEFAULT = 'default';
const SIZE_LG = 'lg';

const TEXT_DEFAULT = 'default';
const TEXT_BOLD = 'bold';

const WIDTH_CONTENT = 'content';
const WIDTH_BLOCK = 'block';

type ButtonVariant =
  | typeof VARIANT_BORDER
  | typeof VARIANT_BORDER_DANGER
  | typeof VARIANT_DANGER
  | typeof VARIANT_DEFAULT
  | typeof VARIANT_LINK_INLINE
  | typeof VARIANT_LINK_INLINE_RED
  | typeof VARIANT_LINK_INLINE_STRONG
  | typeof VARIANT_PRIMARY_DARK
  | typeof VARIANT_PRIMARY_LIGHT;

interface ButtonProps {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  ref?: Ref<HTMLButtonElement>;
  size?: typeof SIZE_DEFAULT | typeof SIZE_LG;
  submit?: boolean;
  testId?: string;
  text?: string;
  variant?: ButtonVariant;
  width?: typeof WIDTH_CONTENT | typeof WIDTH_BLOCK;
}

const Button = ({
  children,
  disabled = false,
  onClick = noop,
  ref,
  size = SIZE_DEFAULT,
  submit = false,
  testId,
  text = TEXT_DEFAULT,
  variant = VARIANT_DEFAULT,
  width = WIDTH_CONTENT,
}: ButtonProps) => (
  <button
    className={classnames(
      'btn btn-reset',
      getVariantClassName(variant),
      getSizeClassName(size),
      getTextClassName(text),
      getWidthClassName(width),
      disabled && getDisabledClassName(variant)
    )}
    data-testid={testId}
    disabled={disabled}
    onClick={onClick}
    ref={ref}
    type={submit ? 'submit' : 'button'}
  >
    {children}
  </button>
);

const getWidthClassName = (width: ButtonProps['width']) => (width === WIDTH_BLOCK ? 'btn-block' : null);

const getSizeClassName = (size: ButtonProps['size']) => (size === SIZE_LG ? 'btn-lg' : null);

const getTextClassName = (text: ButtonProps['text']) => (text === TEXT_BOLD ? 'fw-bold' : null);

const getVariantClassName = (variant: ButtonVariant) => {
  switch (variant) {
    case VARIANT_BORDER:
      return 'btn-outline-primary';
    case VARIANT_BORDER_DANGER:
      return 'btn-outline-danger';
    case VARIANT_DANGER:
      return 'btn-danger';
    case VARIANT_LINK_INLINE:
      return 'btn-link btn-link__inline';
    case VARIANT_LINK_INLINE_RED:
      return 'btn-link btn-link__inline text-red';
    case VARIANT_LINK_INLINE_STRONG:
      return 'btn-link btn-link__inline-strong';
    case VARIANT_PRIMARY_DARK:
      return 'btn-primary-dark disabled:bg-border-gray disabled:border-border-gray opacity-100';
    case VARIANT_PRIMARY_LIGHT:
      return 'btn-primary';
    case VARIANT_DEFAULT:
    // Fallthrough
    default:
      return null;
  }
};

const getDisabledClassName = (variant: ButtonVariant) => {
  switch (variant) {
    case VARIANT_BORDER:
      return 'disabled:border-heading-gray disabled:text-heading-gray';
    case VARIANT_PRIMARY_DARK:
    case VARIANT_PRIMARY_LIGHT:
    case VARIANT_DANGER:
      return 'btn-disabled';
    default:
      return null;
  }
};

export default Button;
